"use client";

import { Input } from "@base-ui/react";
import { Dialog } from "@base-ui/react/dialog";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Loader2, XIcon } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import SearchIcon from "@/components/icons/search";
import Container from "@/components/ui/container";
import type { ApiResponse, PaginatedResult } from "@/lib/fetch";
import { SearchItems, type SearchResult } from "./search-items";
import { SearchPopover, addRecentSearch } from "./search-popover";

const BLUR_TIMEOUT_MS = 200;

const FILTERS = [
  { id: "1", label: "Makale" },
  { id: "2", label: "bebek.com TV" },
  { id: "3", label: "Forum" },
  { id: "4", label: "Alışveriş Rehberi" },
  { id: "6", label: "Quiz" },
];

const InputSearchIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <SearchIcon
    {...props}
    className={`${props.className || ""} [&_path]:stroke-current`}
  />
);

function HeaderSearchContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [activeFilter, setActiveFilter] = useState("");

  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q && q !== activeQuery) {
      setIsOpen(true);
      setSearchQuery(q);
      setActiveQuery(q);
    }
  }, [searchParams, activeQuery]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setSearchQuery("");
      setActiveQuery("");
      setActiveFilter("");
      queryClient.removeQueries({ queryKey: ["search"] });
    } else {
      const currentQ = searchParams.get("q");
      if (currentQ) {
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.delete("q");
        router.replace(
          `${pathname}${newParams.toString() ? `?${newParams.toString()}` : ""}`,
          { scroll: false },
        );
      }
    }
    setIsOpen(open);
  };

  const handleSearch = () => {
    setActiveQuery(searchQuery);
    if (searchQuery.trim()) {
      addRecentSearch(searchQuery.trim());

      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set("q", searchQuery.trim());
      router.replace(`${pathname}?${newParams.toString()}`, { scroll: false });
    } else {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete("q");
      router.replace(
        `${pathname}${newParams.toString() ? `?${newParams.toString()}` : ""}`,
        { scroll: false },
      );
    }
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      enabled: isOpen,
      getNextPageParam: (lastPage: PaginatedResult<SearchResult>) => {
        if (!lastPage) {
          return undefined;
        }
        return lastPage.page < lastPage.totalPages
          ? lastPage.page + 1
          : undefined;
      },
      initialPageParam: 1,
      queryFn: async ({
        pageParam,
      }): Promise<PaginatedResult<SearchResult>> => {
        const filterObj = FILTERS.find((f) => f.label === activeFilter);
        const filterIdQuery = filterObj?.id ? `&FilterId=${filterObj.id}` : "";
        const url = `/api/discover/search?SearchTerm=${activeQuery}&Page=${pageParam}&PageSize=10${filterIdQuery}`;
        const response = await axios
          .get<ApiResponse<PaginatedResult<SearchResult>>>(url)
          .then((res) => res.data);
        if (!response.isSuccess) {
          return {
            data: [],
            page: 1,
            pageSize: 10,
            totalCount: 0,
            totalPages: 1,
          } as PaginatedResult<SearchResult>;
        }
        return response.result;
      },
      queryKey: ["search", activeQuery, activeFilter],
    });

  const results = data?.pages.flatMap((page) => page.data) || [];

  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) {
        observer.current.disconnect();
      }
      if (isFetchingNextPage) {
        return;
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  return (
    <>
      <button
        aria-label="Arama Yap"
        className="border border-brand-blue-200 rounded-full text-brand-blue-600 p-2 font-bold hover:bg-brand-blue-200 transition-colors duration-200 cursor-pointer"
        onClick={() => handleOpenChange(true)}
        type="button"
      >
        <SearchIcon className="size-5" />
      </button>

      <Dialog.Root onOpenChange={handleOpenChange} open={isOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-50 bg-white transition-all duration-200 data-starting-style:opacity-0 data-ending-style:opacity-0" />

          <Dialog.Popup className="fixed inset-0 z-50 flex flex-col bg-white outline-none w-full h-full overflow-hidden transition-all duration-200 data-starting-style:opacity-0 data-starting-style:translate-y-4 data-ending-style:opacity-0 data-ending-style:translate-y-4">
            <Container className="max-w-5xl mt-[55px] flex flex-col h-[calc(100dvh-55px)]">
              <div className="mb-8 flex items-center justify-between shrink-0">
                <h2 className="text-2xl font-bold text-brand-blue-500">
                  Arama
                </h2>
                <Dialog.Close className="rounded-full border border-neutral-200 w-8 h-8 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-50 transition-colors cursor-pointer flex items-center justify-center bg-white">
                  <XIcon className="size-4" />
                </Dialog.Close>
              </div>

              <div className="relative shrink-0">
                <div className="flex items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3.5 focus-within:border-brand-blue-400 focus-within:ring-1 focus-within:ring-brand-blue-400 transition-all bg-white group">
                  <button
                    aria-label="Ara"
                    className="flex items-center justify-center p-1 -ml-1 rounded-full hover:bg-neutral-100 transition-colors cursor-pointer"
                    onClick={handleSearch}
                    type="button"
                  >
                    <InputSearchIcon className="w-5 h-5 text-neutral-400 group-focus-within:text-brand-blue-500 transition-colors" />
                  </button>
                  <Input
                    className="w-full text-base text-neutral-800 placeholder-neutral-400 bg-transparent border-none outline-none focus:outline-none"
                    onBlur={() =>
                      setTimeout(() => setIsFocused(false), BLUR_TIMEOUT_MS)
                    }
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    placeholder="Ara"
                    type="text"
                    value={searchQuery}
                  />
                  {searchQuery && (
                    <button
                      className="text-neutral-400 hover:text-neutral-600 p-1 rounded-full hover:bg-neutral-100 transition-colors cursor-pointer"
                      onClick={() => {
                        setSearchQuery("");
                        setActiveQuery("");
                      }}
                      type="button"
                    >
                      <XIcon className="size-4" />
                    </button>
                  )}
                </div>
                {isFocused && !searchQuery && (
                  <SearchPopover
                    onSelectSearch={(term) => {
                      setSearchQuery(term);
                      setActiveQuery(term);
                      addRecentSearch(term);
                    }}
                  />
                )}
              </div>

              <div
                className="flex items-center flex-nowrap gap-3 overflow-x-auto pt-8 pb-4 shrink-0 scrollbar-hide"
                style={{ msOverflowStyle: "none", scrollbarWidth: "none" }}
              >
                {FILTERS.map((filter) => (
                  <button
                    className={`px-5 py-2 rounded-full border whitespace-nowrap text-sm font-medium transition-colors cursor-pointer ${
                      activeFilter === filter.label
                        ? "border-brand-blue-500 text-brand-blue-500 bg-brand-blue-50/50"
                        : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                    }`}
                    key={filter.label}
                    onClick={() =>
                      setActiveFilter(
                        activeFilter === filter.label ? "" : filter.label,
                      )
                    }
                    type="button"
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-col mt-2 flex-1 overflow-y-auto pb-20 scrollbar-hide" style={{ msOverflowStyle: "none", scrollbarWidth: "none" }}>
                {isLoading && results.length === 0 ? (
                  <div className="flex justify-center items-center py-12 text-neutral-400">
                    <Loader2 className="size-8 animate-spin" />
                  </div>
                ) : (
                  <>
                    <SearchItems results={results} setIsOpen={setIsOpen} />
                    <div
                      className="h-10 w-full shrink-0"
                      ref={lastElementRef}
                    />
                    {isFetchingNextPage && (
                      <div className="flex justify-center items-center py-4 text-neutral-400">
                        <Loader2 className="size-6 animate-spin" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </Container>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export default function HeaderSearch() {
  return (
    <Suspense
      fallback={
        <button
          aria-label="Arama Yap"
          className="border border-brand-blue-200 rounded-full text-brand-blue-600 p-2 font-bold hover:bg-brand-blue-200 transition-colors duration-200 cursor-pointer"
          type="button"
        >
          <SearchIcon className="size-5" />
        </button>
      }
    >
      <HeaderSearchContent />
    </Suspense>
  );
}
